import "./styles.css";
import { getTheme, initTheme, setTheme, type Theme } from "./ui/theme";
import { el } from "./ui/dom";
import { CREATOR } from "./config";
import { mountApp } from "./app";
import { fromBase64Url } from "./crypto/util";
import { TOKEN_BYTES } from "./crypto/keys";
import { ICON } from "./ui/icons";
import { privacyHtml, securityHtml, termsHtml } from "./pages/legal";

initTheme();

const root = document.getElementById("app")!;

// How the page was loaded. A real browser refresh reports "reload".
const navEntry = performance.getEntriesByType("navigation")[0] as
  | PerformanceNavigationTiming
  | undefined;
let firstRoute = true;

/** Go to the share view. This is the first moment the signaling server is used. */
function goShare(): void {
  location.hash = "/share";
}

function scrollTo(id: string) {
  return (e: Event): void => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
}

function themeSeg(): HTMLElement {
  const seg = el("div", { class: "seg" });
  const current = getTheme();
  (["system", "light", "dark"] as Theme[]).forEach((t) => {
    const b = el("button", {
      class: current === t ? "active" : "",
      text: t[0].toUpperCase() + t.slice(1),
    });
    b.addEventListener("click", () => {
      setTheme(t);
      [...seg.children].forEach((c) => c.classList.remove("active"));
      b.classList.add("active");
    });
    seg.append(b);
  });
  return seg;
}

function brand(): HTMLElement {
  return el(
    "a",
    { class: "brand-id", href: "#/", "aria-label": "Enclave home" },
    el("img", { src: "/icon.svg", alt: "" }),
    el("span", { class: "brand-name", text: "Enclave" })
  );
}

function topbar(variant: "landing" | "app"): HTMLElement {
  const right = el("div", { class: "topbar-right" });
  if (variant === "landing") {
    right.append(
      el(
        "nav",
        { class: "topnav" },
        el("a", { href: "#/", text: "How it works", onclick: scrollTo("how") }),
        el("a", { href: "#/security", text: "Security" })
      ),
      themeSeg(),
      el(
        "button",
        { class: "btn btn-primary hide-sm", onclick: goShare },
        "Create link"
      )
    );
  } else {
    right.append(themeSeg());
  }
  return el("div", { class: "topbar" }, brand(), right);
}

function feature(icon: string, title: string, body: string): HTMLElement {
  return el(
    "div",
    { class: "feature" },
    el("div", { class: "f-ico", html: icon }),
    el("h3", { text: title }),
    el("p", { text: body })
  );
}

function step(n: number, title: string, body: string): HTMLElement {
  return el(
    "div",
    { class: "step" },
    el("div", { class: "num", text: String(n) }),
    el("h3", { text: title }),
    el("p", { text: body })
  );
}

function footer(): HTMLElement {
  return el(
    "footer",
    { class: "footer" },
    el(
      "div",
      { class: "footer-brand" },
      el("img", { src: "/icon.svg", alt: "" }),
      el("span", { text: "Enclave" })
    ),
    el("div", {
      class: "footer-tag muted",
      text: "Direct, encrypted, ephemeral file transfer.",
    }),
    el(
      "nav",
      {},
      el("a", { href: "#/security", text: "Security" }),
      el("a", { href: "#/privacy", text: "Privacy" }),
      el("a", { href: "#/terms", text: "Terms" })
    ),
    el(
      "div",
      { class: "muted" },
      "Built by ",
      el("a", {
        href: CREATOR.portfolio,
        target: "_blank",
        rel: "noopener",
        text: CREATOR.name,
      }),
      " \u00b7 ",
      el("a", {
        href: CREATOR.linkedin,
        target: "_blank",
        rel: "noopener",
        text: "LinkedIn",
      })
    )
  );
}

function landing(): HTMLElement {
  const hero = el(
    "section",
    { class: "lhero" },
    el(
      "h1",
      {},
      "Send files, ",
      el("span", { class: "accent", text: "end to end." })
    ),
    el("p", {
      class: "sub",
      text: "Enclave moves files straight from your device to theirs over an encrypted, peer-to-peer connection. One link, no accounts, nothing stored.",
    }),
    el(
      "div",
      { class: "cta-row" },
      el(
        "button",
        { class: "btn btn-primary btn-lg", onclick: goShare },
        el("span", { html: ICON.upload }),
        "Create a share link"
      ),
      el(
        "a",
        { class: "btn btn-lg", href: "#/", onclick: scrollTo("how") },
        "How it works"
      )
    ),
    el(
      "div",
      { class: "trustline" },
      el("span", {}, el("span", { html: ICON.check }), "End-to-end encrypted"),
      el("span", {}, el("span", { html: ICON.check }), "No sign-up"),
      el("span", {}, el("span", { html: ICON.check }), "Nothing stored")
    )
  );

  const features = el(
    "section",
    { class: "section" },
    el("h2", { class: "section-title", text: "Private by design" }),
    el("p", {
      class: "section-sub",
      text: "Security isn\u2019t a setting here \u2014 it\u2019s the architecture.",
    }),
    el(
      "div",
      { class: "features" },
      feature(
        ICON.lock,
        "End-to-end encrypted",
        "Files are sealed with XChaCha20-Poly1305 and an authenticated X25519 handshake. Only you and the recipient can read them."
      ),
      feature(
        ICON.bolt,
        "Direct peer-to-peer",
        "Data travels browser-to-browser over WebRTC. Our server only introduces the two of you, then steps out of the way."
      ),
      feature(
        ICON.sparkle,
        "Ephemeral",
        "A one-time link, no accounts, no storage. The connection \u2014 and everything in it \u2014 vanishes when you\u2019re done."
      ),
      feature(
        ICON.globe,
        "Works everywhere",
        "Any modern browser on Windows, macOS, Linux, Android, or iOS. Install it as an app, or just open the link."
      )
    )
  );

  const how = el(
    "section",
    { class: "section", id: "how" },
    el("h2", { class: "section-title", text: "How it works" }),
    el("p", { class: "section-sub", text: "Three steps, about ten seconds." }),
    el(
      "div",
      { class: "steps" },
      step(
        1,
        "Create a link",
        "Click create and your browser mints a secret, one-time link \u2014 generated locally, never on our server."
      ),
      step(
        2,
        "Share it",
        "Send the link any way you like, or have them scan the QR code in person. It works exactly once."
      ),
      step(
        3,
        "Transfer directly",
        "They open it, your devices connect peer-to-peer, and files move encrypted end to end."
      )
    )
  );

  const security = el(
    "section",
    { class: "section" },
    el(
      "div",
      { class: "secband" },
      el("div", {
        class: "f-ico",
        html: ICON.shield,
        style: "margin:0 auto 14px",
      }),
      el("h2", { text: "Built so you don\u2019t have to trust the server" }),
      el("p", {
        text: "The secret that protects your files lives in the link and never reaches us. We run a blind relay that only helps two browsers find each other \u2014 it can\u2019t read, store, or tamper with anything.",
      }),
      el("a", { class: "btn", href: "#/security" }, "Read the security model")
    )
  );

  const cta = el(
    "section",
    { class: "ctaband" },
    el("h2", { text: "Ready to send something?" }),
    el(
      "button",
      { class: "btn btn-primary btn-lg", onclick: goShare },
      el("span", { html: ICON.upload }),
      "Create a share link"
    )
  );

  return el(
    "div",
    { class: "landing" },
    topbar("landing"),
    hero,
    features,
    how,
    security,
    cta,
    footer()
  );
}

function renderLanding(): void {
  root.replaceChildren(landing());
}

function renderLegal(html: string): void {
  root.replaceChildren(el("div", { class: "legal", html }));
}

function renderApp(token: Uint8Array | null): void {
  const isJoin = token !== null;
  const shell = el(
    "div",
    { class: "app" },
    topbar("app"),
    el(
      "div",
      { class: "apptitle" },
      el("h1", { text: isJoin ? "Incoming transfer" : "Create a share link" }),
      el("p", {
        text: isJoin
          ? "Connect to receive the files securely."
          : "Pick files, then send the link or QR. It works once.",
      })
    ),
    el(
      "div",
      { class: "warn-banner" },
      el("span", { html: ICON.alert }),
      el("span", {
        text: isJoin
          ? "This link works once \u2014 don\u2019t refresh. Reloading will expire it."
          : "Keep this tab open. Refreshing generates a new link and the current one stops working.",
      })
    )
  );
  const content = el("div");
  shell.append(content, footer());
  root.replaceChildren(shell);
  mountApp(content, token);
}

function renderExpired(): void {
  const card = el(
    "div",
    { class: "expired" },
    el("div", { class: "expired-ico", html: ICON.clock }),
    el("h1", { text: "This link has expired" }),
    el("p", {
      text: "Enclave links work once and can\u2019t be reopened or refreshed. Ask the sender for a new link, or create your own to share.",
    }),
    el(
      "div",
      { class: "cta-row" },
      el(
        "button",
        { class: "btn btn-primary btn-lg", onclick: goShare },
        el("span", { html: ICON.upload }),
        "Create a share link"
      ),
      el("a", { class: "btn btn-lg", href: "#/" }, "Back to home")
    )
  );
  root.replaceChildren(
    el("div", { class: "app" }, topbar("app"), card, footer())
  );
}

function route(): void {
  const reload = firstRoute && navEntry?.type === "reload";
  firstRoute = false;
  const hash = location.hash.slice(1);
  if (hash === "" || hash === "/") return renderLanding();
  if (hash === "/share") return renderApp(null);
  if (hash === "/terms") return renderLegal(termsHtml());
  if (hash === "/privacy") return renderLegal(privacyHtml());
  if (hash === "/security") return renderLegal(securityHtml());
  if (hash.startsWith("/")) return renderLanding(); // unknown route

  // Otherwise the fragment is a join token.
  try {
    const token = fromBase64Url(hash);
    if (token.length === TOKEN_BYTES) {
      // A refresh can't resume a one-time link yet — show the expired notice.
      return reload ? renderExpired() : renderApp(token);
    }
  } catch {
    /* fall through to landing */
  }
  renderLanding();
}

route();
window.addEventListener("hashchange", route);
